import { state } from "../state.js";
import { audioCtx, getBuffer } from "./context.js";
import { playRealisticCannon } from "./realistic.js";
import {
  playSynthExplosion8Bit,
  playSynthLose8Bit,
  playSynthOof8Bit,
  playSynthPew,
  playSynthPew8Bit,
  playSynthPowerup8Bit,
  playSynthReloading8Bit,
  playSynthWin8Bit,
} from "./retro.js";
import {
  playSynthExplosionModern,
  playSynthLoseModern,
  playSynthOofModern,
  playSynthPewModern,
  playSynthPowerupModern,
  playSynthReloadingModern,
  playSynthWinModern,
} from "./modern.js";
import { playSynthLaserCharge, playSynthPickup, playSynthWeaponPew } from "./synth.js";
import { refreshVroomGain } from "./vroom.js";

export let globalSfxVolume = 1.0;

export function setSfxVolume(vol: number) {
  globalSfxVolume = vol;
  refreshVroomGain();
}

/** Instantly play a sound effect from memory. `rate` is playback speed (1 = normal). */
export function playSfx(name: string, volume = 1.0, rate = 1.0) {
  if (name === "laser_charge") {
    playSynthLaserCharge(volume);
    return;
  }
  if (name.startsWith("pickup_")) {
    playSynthPickup(name.replace("pickup_", ""), volume);
    return;
  }
  if (name.startsWith("pew_")) {
    playSynthWeaponPew(name.replace("pew_", ""), volume, rate);
    return;
  }
  if (state.realisticEnabled) {
    if (name === "pew") {
      playRealisticCannon(volume, rate);
      return;
    }
    const buffer = getBuffer(name);
    if (buffer) {
      if (audioCtx.state === "suspended") audioCtx.resume();
      const source = audioCtx.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = rate;
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = volume * globalSfxVolume;
      source.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      source.start();
      return;
    }
  }

  if (state.modernEnabled) {
    if (name === "pew") {
      playSynthPewModern(volume, rate);
      return;
    }
    if (name === "explosion") {
      playSynthExplosionModern(volume);
      return;
    }
    if (name === "powerup") {
      playSynthPowerupModern(volume);
      return;
    }
    if (name === "oof") {
      playSynthOofModern(volume);
      return;
    }
    if (name === "reloading") {
      playSynthReloadingModern(volume);
      return;
    }
    if (name === "win") {
      playSynthWinModern(volume);
      return;
    }
    if (name === "lose") {
      playSynthLoseModern(volume);
      return;
    }
  }

  if (state.battleCityEnabled) {
    if (name === "pew") {
      playSynthPew8Bit(volume, rate);
      return;
    }
    if (name === "explosion") {
      playSynthExplosion8Bit(volume);
      return;
    }
    if (name === "powerup") {
      playSynthPowerup8Bit(volume);
      return;
    }
    if (name === "oof") {
      playSynthOof8Bit(volume);
      return;
    }
    if (name === "reloading") {
      playSynthReloading8Bit(volume);
      return;
    }
    if (name === "win") {
      playSynthWin8Bit(volume);
      return;
    }
    if (name === "lose") {
      playSynthLose8Bit(volume);
      return;
    }
  }

  if (name === "pew") {
    playSynthPew(volume, rate);
    return;
  }

  const buffer = getBuffer(name);
  if (!buffer) return;
  if (audioCtx.state === "suspended") audioCtx.resume();

  const source = audioCtx.createBufferSource();
  source.buffer = buffer;
  source.playbackRate.value = rate;
  const gainNode = audioCtx.createGain();
  gainNode.gain.value = volume * globalSfxVolume;

  source.connect(gainNode);
  gainNode.connect(audioCtx.destination);
  source.start();
}
